import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Minus, X, Search } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { refreshAdminNotifications } from "@/components/NotificationBell";
import "./SalesCalculator.css";

const PREVIEW_DEBOUNCE_MS = 400;

function won(n) {
  return `₩ ${Number(n || 0).toLocaleString()}`;
}

function linePayload(l) {
  const row = { master_id: l.master_id, quantity: l.quantity };
  const raw = l.unit_price;
  if (raw === "" || raw == null) return row;
  const n = Number(raw);
  if (Number.isFinite(n) && n > 0) row.unit_price = n;
  return row;
}

function rangeLabel(item) {
  if (item.pricing_type === "PERCENT") {
    return `+${item.recommended_price_min}~${item.recommended_price_max}%`;
  }
  if (item.pricing_type === "CUSTOM" && !item.recommended_price_min) {
    return "별도 협의";
  }
  const a = item.recommended_price_min || 0;
  const b = item.recommended_price_max || a;
  if (a === b) return won(a);
  return `${won(a)} ~ ${won(b)}`;
}

function unitLabel(unit) {
  return ({ page: "P", section: "Section", type: "종류", language: "언어", month: "월", year: "년", set: "식", item: "개", hour: "시간" }[unit] || unit);
}

function roleLabel(item) {
  const role = item?.quote_role;
  return ({
    BASE: "BASE",
    INCLUDED: "포함",
    ADD_ON: "ADD-ON",
    SETUP: "구축",
    DSITE_RECURRING: "DSITE 월/연",
    CUSTOMER_DIRECT: "고객 직접",
    USAGE_BASED: "사용량",
  }[role] || "");
}

function isCanceled(err) {
  return err?.code === "ERR_CANCELED" || err?.name === "CanceledError" || err?.name === "AbortError";
}

export default function SalesCalculator({ customers = [], viewer = "sales", onConverted, prefillLines = null }) {
  const { user } = useAuth();
  const canDuration = user?.role === "super_admin" || user?.sales_level === "sales_super_admin";
  const [catalog, setCatalog] = useState({ categories: [], items: [], viewer });
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState("");
  const [categoryId, setCategoryId] = useState("homepage");
  const [q, setQ] = useState("");
  const [lines, setLines] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewPending, setPreviewPending] = useState(false);
  const [offer, setOffer] = useState("");
  const [fieldMode, setFieldMode] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [converting, setConverting] = useState(false);
  const [convertForm, setConvertForm] = useState({ title: "", customer_id: "", customer_email: "", notes: "" });
  const [durationNote, setDurationNote] = useState("");
  const [resetOpen, setResetOpen] = useState(false);
  const previewAbortRef = useRef(null);
  const previewSeqRef = useRef(0);
  const convertingRef = useRef(false);
  const prefillDoneRef = useRef(false);
  const [reviewPrefill, setReviewPrefill] = useState(null);

  const load = async () => {
    setCatalogLoading(true);
    setCatalogError("");
    try {
      const r = await api.get("/admin/sales-catalog");
      setCatalog(r.data);
      if (r.data.categories?.[0] && !categoryId) setCategoryId(r.data.categories[0].id);
    } catch {
      setCatalogError("영업 카탈로그를 불러오지 못했습니다.");
      toast.error("영업 카탈로그를 불러오지 못했습니다.");
    } finally {
      setCatalogLoading(false);
    }
  };
  useEffect(() => {
    load();
    return () => {
      previewAbortRef.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const byId = useMemo(() => {
    const m = {};
    (catalog.items || []).forEach((it) => {
      m[it.id] = it;
      m[it.code] = it;
    });
    return m;
  }, [catalog.items]);

  const selectedBase = useMemo(
    () => lines.map((l) => byId[l.master_id]).find((m) => m?.quote_role === "BASE"),
    [lines, byId],
  );
  const hasBackendFeature = lines.some((l) => byId[l.master_id]?.requires_backend);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const staticSite = Boolean(selectedBase?.static_site && !hasBackendFeature);
    return (catalog.items || []).filter((it) => {
      if (staticSite && it.code === "infra.backend") return false;
      if (categoryId && it.category_id !== categoryId) return false;
      if (!needle) return true;
      return `${it.name} ${it.name_en} ${it.sales_description}`.toLowerCase().includes(needle);
    });
  }, [catalog.items, categoryId, q, selectedBase, hasBackendFeature]);

  const selectedCodes = useMemo(() => new Set(lines.map((l) => l.master_id)), [lines]);

  const refreshPreview = async (nextLines, nextOffer) => {
    if (!nextLines.length) {
      previewAbortRef.current?.abort();
      setPreview(null);
      setPreviewPending(false);
      return;
    }
    previewAbortRef.current?.abort();
    const ac = new AbortController();
    previewAbortRef.current = ac;
    const seq = ++previewSeqRef.current;
    setPreviewPending(true);
    try {
      const r = await api.post("/admin/sales-calculator/preview", {
        lines: nextLines.map(linePayload),
        sales_offer_price: nextOffer === "" || nextOffer == null ? undefined : Number(nextOffer),
        duration_override: canDuration ? (durationNote || undefined) : undefined,
      }, { signal: ac.signal });
      if (seq !== previewSeqRef.current) return;
      setPreview(r.data);
    } catch (err) {
      if (isCanceled(err) || seq !== previewSeqRef.current) return;
      setPreview(null);
    } finally {
      if (seq === previewSeqRef.current) setPreviewPending(false);
    }
  };

  useEffect(() => {
    if (!lines.length) {
      previewAbortRef.current?.abort();
      setPreview(null);
      setPreviewPending(false);
      return undefined;
    }
    const timer = setTimeout(() => {
      refreshPreview(lines, offer);
    }, PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines, offer, durationNote]);

  useEffect(() => {
    if (!(catalog.items || []).length || prefillDoneRef.current) return;
    try {
      let mapped = null;
      const raw = sessionStorage.getItem("dsite_calc_prefill");
      if (raw) {
        sessionStorage.removeItem("dsite_calc_prefill");
        mapped = JSON.parse(raw);
      } else if (prefillLines?.length) {
        mapped = { lines: prefillLines };
      }
      if (!mapped) return;
      prefillDoneRef.current = true;
      const next = (mapped.lines || []).map((l) => {
        const item = (catalog.items || []).find((it) => it.code === l.master_id || it.id === l.master_id);
        return { master_id: item?.id || l.master_id, quantity: l.quantity || 1, unit_price: "", quote_role: item?.quote_role };
      }).filter((l) => l.master_id);
      const rest = next.filter((l) => l.quote_role !== "BASE").map(({ quote_role, ...l }) => l);
      const lastBase = [...next].reverse().find((l) => l.quote_role === "BASE");
      const collapsed = lastBase
        ? [...rest, { master_id: lastBase.master_id, quantity: 1, unit_price: "" }]
        : rest;
      const merged = [];
      const seen = new Map();
      collapsed.forEach((l) => {
        const prev = seen.get(l.master_id);
        if (prev) {
          prev.quantity = Math.max(Number(prev.quantity) || 1, Number(l.quantity) || 1);
          return;
        }
        const row = { ...l, quantity: Number(l.quantity) || 1 };
        seen.set(l.master_id, row);
        merged.push(row);
      });
      if (merged.length) {
        setLines(merged);
        toast.success(`선택 항목 ${merged.length}개를 가져왔습니다.`);
      }
      const reviewItems = mapped.unpriced_gap_items || mapped.tech_review_items || mapped.custom_needed || [];
      if (mapped.route_type || reviewItems.length) {
        setReviewPrefill({
          route_type: mapped.route_type || "",
          confirmed_scope_only: Boolean(mapped.confirmed_scope_only),
          estimate_scope: mapped.estimate_scope || "",
          price_confidence: mapped.price_confidence || "",
          display_intent: mapped.display_intent || "",
          recommended_product: mapped.recommended_product || "",
          tech_review_items: mapped.tech_review_items || [],
          unpriced_gap_items: mapped.unpriced_gap_items || mapped.custom_needed || [],
        });
      }
      if (mapped.custom_needed?.length) toast.message(`별도 견적 필요: ${mapped.custom_needed.join(", ")}`);
    } catch {
      /* ignore */
    }
  }, [catalog.items, prefillLines]);

  const addItem = (item) => {
    const id = item.id || item.code;
    if (item.quote_role === "BASE") {
      const rest = lines.filter((l) => byId[l.master_id]?.quote_role !== "BASE");
      setLines([...rest, { master_id: id, quantity: 1, unit_price: "" }]);
      return;
    }
    if (item.exclusive_group && Number(item.tier_rank) > 0) {
      const rest = lines.filter((l) => {
        const m = byId[l.master_id];
        return m?.exclusive_group !== item.exclusive_group;
      });
      setLines([...rest, { master_id: id, quantity: 1, unit_price: "" }]);
      return;
    }
    const existing = lines.find((l) => l.master_id === id);
    if (existing) {
      toast.message("이미 선택된 항목입니다. 수량은 오른쪽에서 변경하세요.");
      return;
    }
    setLines([...lines, { master_id: id, quantity: item.default_quantity || 1, unit_price: "" }]);
  };

  const setQty = (id, qty) => {
    if (byId[id]?.quote_role === "BASE") return;
    const n = Math.max(1, Number(qty) || 1);
    setLines(lines.map((l) => (l.master_id === id ? { ...l, quantity: n } : l)));
  };

  const removeLine = (id) => setLines(lines.filter((l) => l.master_id !== id));

  const suggestions = useMemo(() => {
    const codes = new Set();
    lines.forEach((l) => {
      const m = byId[l.master_id];
      (m?.suggest || []).forEach((c) => {
        if (!selectedCodes.has(c) && !selectedCodes.has(byId[c]?.id)) codes.add(c);
      });
    });
    return [...codes].map((c) => byId[c]).filter(Boolean);
  }, [lines, byId, selectedCodes]);

  const allowConfirmedScopeConvert = Boolean(
    reviewPrefill?.confirmed_scope_only || reviewPrefill?.route_type === "FULL_CUSTOM_REVIEW"
  );

  const openConvert = () => {
    if (converting) return;
    if (lines.length === 0) return toast.error("항목을 먼저 선택해 주세요.");
    if (!allowConfirmedScopeConvert && (preview?.base_count ?? 0) !== 1) {
      return toast.error("기본 홈페이지 유형을 먼저 선택해주세요.");
    }
    if (allowConfirmedScopeConvert && !(Number(preview?.sales_offer_price || 0) > 0)) {
      return toast.error("현재 가격을 산정할 수 있는 기능 범위가 없습니다. 개발 담당 기술 검토 후 견적을 작성해주세요.");
    }
    setConvertForm({
      title: convertForm.title || "웹사이트 제작 견적",
      customer_id: convertForm.customer_id,
      customer_email: convertForm.customer_email,
      notes: convertForm.notes,
    });
    setConvertOpen(true);
  };

  const submitConvert = async (e) => {
    e.preventDefault();
    if (convertingRef.current) return;
    if (!convertForm.title.trim()) return toast.error("견적 제목이 필요합니다.");
    convertingRef.current = true;
    setConverting(true);
    try {
      const r = await api.post("/admin/sales-calculator/convert", {
        title: convertForm.title,
        customer_id: convertForm.customer_id || undefined,
        customer_email: convertForm.customer_email || undefined,
        notes: convertForm.notes || undefined,
        sales_offer_price: offer === "" ? undefined : Number(offer),
        recommended_product: reviewPrefill?.recommended_product || undefined,
        display_intent: reviewPrefill?.display_intent || undefined,
        route_type: reviewPrefill?.route_type || undefined,
        price_confidence: reviewPrefill?.price_confidence || undefined,
        tech_review_items: reviewPrefill?.tech_review_items || undefined,
        unpriced_gap_items: reviewPrefill?.unpriced_gap_items || undefined,
        confirmed_scope_only: reviewPrefill?.confirmed_scope_only || undefined,
        estimate_scope: reviewPrefill?.estimate_scope || undefined,
        lines: lines.map(linePayload),
      });
      toast.success("견적서가 생성되었습니다.");
      setConvertOpen(false);
      refreshAdminNotifications();
      onConverted?.(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "전환 실패");
    } finally {
      convertingRef.current = false;
      setConverting(false);
    }
  };

  const resetLocalSession = () => {
    previewAbortRef.current?.abort();
    previewSeqRef.current += 1;
    setLines([]);
    setPreview(null);
    setPreviewPending(false);
    setOffer("");
    setDurationNote("");
    setConvertOpen(false);
    setConvertForm({ title: "", customer_id: "", customer_email: "", notes: "" });
    setFieldMode(false);
    setResetOpen(false);
    setReviewPrefill(null);
  };

  const p = preview || {};
  const isOwner = catalog.viewer === "owner";
  const catalogReady = !catalogLoading && !catalogError && (catalog.items || []).length > 0;
  const showEmptyTotals = catalogLoading || !lines.length;
  const canOpenConvert = allowConfirmedScopeConvert
    ? (Number(p.sales_offer_price || 0) > 0)
    : (p.base_count ?? 0) === 1;

  if (fieldMode) {
    return (
      <div className="sales-field" data-testid="sales-field-mode">
        <div className="sales-field__bar">
          <div className="sales-field__brand">DSITESOLUTION</div>
          <button type="button" className="sales-field__exit" onClick={() => setFieldMode(false)}>상담 종료</button>
        </div>
        <div className="sales-field__card">
          <div className="sales-field__kicker">예상 프로젝트 견적</div>
          <div className="sales-field__title">WEB DEVELOPMENT</div>
          <div className="sales-field__functions">
            {(p.lines || []).map((l) => (
              <div key={l.master_id} className="sales-field__fn">
                {l.name}{l.quantity > 1 ? ` × ${l.quantity}` : ""}
              </div>
            ))}
          </div>
          <div className="sales-field__rule" />
          <div className="sales-field__label">초기 제작비</div>
          <div className="sales-field__range">
            {won(p.recommended_min_total)}
            <span> ~ </span>
            {won(p.recommended_max_total)}
          </div>
          {p.sales_offer_price ? (
            <>
              <div className="sales-field__label">제안 금액 (VAT 별도)</div>
              <div className="sales-field__offer">{won(p.sales_offer_price)}</div>
              <div className="sales-field__vat">부가세 10% {won(p.vat_amount)} · 포함 {won(p.total_with_vat)}</div>
            </>
          ) : null}
          <div className="sales-field__rule" />
          <div className="sales-field__row">
            <span>DSITE 월 유지/관리비</span>
            <strong>{won(p.monthly_min)} ~ {won(p.monthly_max)}</strong>
          </div>
          <div className="sales-field__row">
            <span>고객 직접 결제 외부 서비스</span>
            <strong>{(p.customer_direct_labels || []).length ? (p.customer_direct_labels || []).join(", ") : "해당 없음"}</strong>
          </div>
          <div className="sales-field__row">
            <span>사용량 기반 비용</span>
            <strong>{(p.usage_based || []).length ? (p.usage_based || []).map((u) => u.name).join(", ") : "해당 없음"}</strong>
          </div>
          <div className="sales-field__row">
            <span>예상 제작기간</span>
            <strong>협의 후 확정</strong>
          </div>
          {canDuration && (durationNote || p.duration_hint) ? (
            <div className="sales-field__row">
              <span>참고 예상기간</span>
              <strong>{durationNote || p.duration_hint}</strong>
            </div>
          ) : null}
          <p className="sales-field__disclaimer">
            표시 금액은 상담 단계의 예상 견적이며, 상세 요구사항 확인 후 변경될 수 있습니다. 제작기간은 상세 협의 후 확정됩니다.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="sales-calc" data-testid="sales-calculator">
      <div className="sales-calc__head">
        <div>
          <h3 className="font-display text-lg">영업 견적 계산기</h3>
          <p className="text-xs text-white/40 mt-1">현장에서 기능을 선택하면 예상 범위가 바로 계산됩니다. 기존 견적 작성도 그대로 사용할 수 있습니다.</p>
        </div>
      </div>

      {catalogLoading && (
        <div className="sales-calc__loading" data-testid="sales-catalog-loading" role="status" aria-live="polite">
          카탈로그를 불러오는 중입니다...
        </div>
      )}
      {catalogError && (
        <div className="sales-calc__loading sales-calc__loading--error" data-testid="sales-catalog-error">
          <span>{catalogError}</span>
          <button type="button" className="sales-calc__btn sales-calc__btn--ghost" onClick={load}>다시 시도</button>
        </div>
      )}

      <div className={`sales-calc__grid${catalogLoading ? " is-loading" : ""}`}>
        <aside className="sales-calc__cats">
          {catalogLoading && [0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
            <div key={i} className="sales-calc__skel sales-calc__skel--cat" />
          ))}
          {!catalogLoading && (catalog.categories || []).map((c) => (
            <button
              key={c.id}
              type="button"
              className={categoryId === c.id ? "is-active" : ""}
              onClick={() => setCategoryId(c.id)}
            >
              {c.label}
            </button>
          ))}
        </aside>

        <section className="sales-calc__list">
          <div className="sales-calc__search">
            <Search size={14} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="기능 검색" disabled={!catalogReady} />
          </div>
          {suggestions.length > 0 && (
            <div className="sales-calc__suggest">
              <div className="sales-calc__suggest-label">함께 검토</div>
              {suggestions.map((s) => (
                <button key={s.id} type="button" onClick={() => addItem(s)}>{s.name}</button>
              ))}
            </div>
          )}
          <div className="sales-calc__items">
            {catalogLoading && [0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="sales-calc__item">
                <div className="sales-calc__skel-stack">
                  <div className="sales-calc__skel sales-calc__skel--line" />
                  <div className="sales-calc__skel sales-calc__skel--line sales-calc__skel--short" />
                </div>
                <div className="sales-calc__skel sales-calc__skel--btn" />
              </div>
            ))}
            {catalogReady && filtered.map((item) => (
              <div key={item.id} className="sales-calc__item">
                <div>
                  <div className="sales-calc__item-name">{item.name}{roleLabel(item) ? <em> {roleLabel(item)}</em> : ""}</div>
                  <div className="sales-calc__item-desc">{item.sales_description || item.name_en}</div>
                  <div className="sales-calc__item-price">{rangeLabel(item)} {item.unit ? `/ ${unitLabel(item.unit)}` : ""}{item.included_pages ? ` · 포함 ${item.included_pages}p` : ""}</div>
                </div>
                <button type="button" className="sales-calc__add" onClick={() => addItem(item)} data-testid={`sales-add-${item.code}`}>
                  <Plus size={14} />
                </button>
              </div>
            ))}
          </div>
        </section>

        <aside className="sales-calc__sum">
          <div className="sales-calc__sum-head">
            <div className="sales-calc__sum-title">예상 견적</div>
            <button
              type="button"
              className="sales-calc__btn sales-calc__btn--ghost sales-calc__btn--reset"
              onClick={() => setResetOpen(true)}
              data-testid="sales-calc-reset"
              disabled={catalogLoading}
            >
              전체 리셋
            </button>
          </div>
          {catalogLoading ? (
            <div className="sales-calc__sum-wait">카탈로그 로딩 중...</div>
          ) : showEmptyTotals ? (
            <div className="sales-calc__sum-wait">항목을 선택하면 예상 금액이 계산됩니다.</div>
          ) : (
            <>
              <p className="sales-calc__provisional">{p.provisional_notice || "본 견적은 상담 단계의 임시 견적이며, 내부 기술 검토 및 최종 요구사항 확인 후 금액이 조정될 수 있습니다."}</p>
              {(reviewPrefill?.unpriced_gap_items || []).length ? (
                <div className="sales-calc__provisional" data-testid="sales-expert-review-items">
                  <strong>추가 기술검토 항목</strong>
                  <ul>
                    {reviewPrefill.unpriced_gap_items.map((item) => <li key={item}>{item}</li>)}
                  </ul>
                </div>
              ) : null}
              {p.custom_consult_required && (
                <p className="sales-calc__provisional">{p.custom_consult_notice}</p>
              )}
              {previewPending && !preview && (
                <div className="sales-calc__sum-wait">예상 금액을 계산하는 중...</div>
              )}
              <div className="sales-calc__kv"><span>초기 제작비</span><b>{won(p.recommended_min_total)} ~ {won(p.recommended_max_total)}</b></div>
              <div className="sales-calc__kv" data-testid="sales-supply-from-preview"><span>공급가</span><b>{won(p.sales_offer_price)}</b></div>
            </>
          )}
          <label className="sales-calc__offer">
            제안가 수정 (선택)
            <input type="number" value={offer} onChange={(e) => setOffer(e.target.value)} placeholder="비우면 위 공급가 사용" disabled={catalogLoading} />
          </label>
          {!catalogLoading && !showEmptyTotals && (
            <>
              <div className="sales-calc__kv"><span>VAT 10%</span><b>{won(p.vat_amount)}</b></div>
              <div className="sales-calc__kv sales-calc__kv--total"><span>VAT 포함</span><b>{won(p.total_with_vat)}</b></div>
              <div className="sales-calc__bucket">DSITE 월 유지/관리비</div>
              <div className="sales-calc__kv"><span>월</span><b>{won(p.monthly_min)} ~ {won(p.monthly_max)}</b></div>
              <div className="sales-calc__bucket">고객 직접 결제 외부 서비스</div>
              <div className="sales-calc__kv"><span>항목</span><b>{(p.customer_direct_labels || []).length ? (p.customer_direct_labels || []).join(", ") : "-"}</b></div>
              <div className="sales-calc__bucket">사용량 기반 비용</div>
              <div className="sales-calc__kv"><span>항목</span><b>{(p.usage_based || []).length ? (p.usage_based || []).map((u) => u.name).join(", ") : "-"}</b></div>
              <div className="sales-calc__kv"><span>예상 제작기간</span><b>협의 후 확정</b></div>
            </>
          )}
          {canDuration && (
            <label className="sales-calc__offer">
              참고 예상기간
              <input value={durationNote} onChange={(e) => setDurationNote(e.target.value)} placeholder={p.duration_hint || "선택 입력"} disabled={catalogLoading} />
            </label>
          )}
          {(p.needs_review || []).map((n, i) => (
            <div key={`rev-${i}`} className="sales-calc__warn">{n.reason}</div>
          ))}
          {p.approval_status && p.approval_status !== "NORMAL" && (
            <div className="sales-calc__warn">현재 제안금액은 상위 관리자 확인이 필요합니다.</div>
          )}
          {(p.packages || []).includes("pkg.pg") && (
            <div className="sales-calc__warn">PG 패키지에 주문 DB·승인·취소·환불이 포함됩니다. 별도 가산하지 않습니다.</div>
          )}
          {isOwner && p.owner && (
            <div className="sales-calc__owner">
              <div>OWNER 내부</div>
              <div>원가 {p.owner.cost_incomplete ? "원가 미입력" : won(p.owner.cost_total)}</div>
              {p.owner.internal_min_total ? <div>내부 최소 {won(p.owner.internal_min_total)}</div> : <div>내부 최소 미입력</div>}
              <div>공급가 {won(p.owner.supply)}</div>
              {p.owner.cost_incomplete ? (
                <div>예상 이익 / 마진 — 원가 미입력</div>
              ) : (
                <div>이익 {won(p.owner.profit)} {p.owner.margin_rate != null ? `(${p.owner.margin_rate}%)` : ""}</div>
              )}
            </div>
          )}
          <div className="sales-calc__picked">
            {(p.lines || []).map((l) => (
              <div key={l.master_id} className="sales-calc__picked-row">
                <span>
                  {l.name}{l.quantity > 1 ? ` × ${l.quantity}` : ""}
                  {l.waive_reason ? ` · ${l.waive_reason}` : ""}
                  {l.auto_added ? " · 자동 1식" : ""}
                </span>
                {l.auto_added || l.quote_role === "BASE" ? (
                  <span className="sales-calc__qty">{l.quote_role === "BASE" ? "1식" : `×${l.quantity}`}</span>
                ) : (
                  <span className="sales-calc__qty">
                    <button type="button" onClick={() => setQty(l.master_id, l.quantity - 1)}><Minus size={12} /></button>
                    {l.quantity}
                    <button type="button" onClick={() => setQty(l.master_id, l.quantity + 1)}><Plus size={12} /></button>
                  </span>
                )}
                {l.auto_added ? <span /> : (
                  <button type="button" onClick={() => removeLine(l.master_id)}><X size={12} /></button>
                )}
              </div>
            ))}
          </div>
          <button type="button" className="sales-calc__btn sales-calc__btn--ghost" onClick={() => setFieldMode(true)} data-testid="sales-field-open" disabled={catalogLoading || converting}>현장 상담 모드</button>
          <button type="button" className="sales-calc__btn" onClick={openConvert} data-testid="sales-convert-open" disabled={catalogLoading || converting || !canOpenConvert}>
            {converting ? "견적서 생성 중..." : "견적서로 전환"}
          </button>
          <p className="sales-calc__legal">
            본 금액은 상담 단계에서 고객 요구사항을 기준으로 산정한 예상 견적입니다. 실제 제작비 및 제작기간은 상세 요구사항, 디자인 범위, 기능 난이도, 제공 자료, 외부 서비스 및 개발환경 확인 후 최종 견적서와 계약서를 통해 확정됩니다.
          </p>
        </aside>
      </div>

      {convertOpen && (
        <div className="sales-calc__modal" data-testid="sales-convert-modal">
          <form className="sales-calc__modal-card" onSubmit={submitConvert}>
            <div className="sales-calc__modal-head">
              <h4>정식 견적서로 전환</h4>
            </div>
            <div className="sales-calc__modal-body">
              <input placeholder="견적 제목" value={convertForm.title} onChange={(e) => setConvertForm({ ...convertForm, title: e.target.value })} disabled={converting} />
              <select value={convertForm.customer_id} onChange={(e) => setConvertForm({ ...convertForm, customer_id: e.target.value })} disabled={converting}>
                <option value="">고객 선택 (선택)</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.name} - {c.email}</option>)}
              </select>
              {!convertForm.customer_id && (
                <input placeholder="고객 이메일" value={convertForm.customer_email} onChange={(e) => setConvertForm({ ...convertForm, customer_email: e.target.value })} disabled={converting} />
              )}
              <div className="text-xs text-white/45">Range 항목은 실제 단가 1개를 지정합니다.</div>
              {lines.map((l) => {
                const m = byId[l.master_id];
                return (
                  <label key={l.master_id} className="sales-calc__price-row">
                    <span>
                      {m?.name} × {l.quantity}
                      <em>{m ? ` 권장 ${rangeLabel(m)}` : ""}</em>
                    </span>
                    <input type="number" value={l.unit_price} placeholder={m?.pricing_type === "PERCENT" ? "수동 입력" : "단가"} disabled={converting} onChange={(e) => setLines(lines.map((x) => x.master_id === l.master_id ? { ...x, unit_price: e.target.value } : x))} />
                  </label>
                );
              })}
              <label className="sales-calc__modal-notes-label" htmlFor="sales-convert-notes">고객 비고</label>
              <textarea
                id="sales-convert-notes"
                className="sales-calc__modal-notes"
                rows={5}
                placeholder="고객 요청사항, 협의 메모"
                value={convertForm.notes}
                onChange={(e) => setConvertForm({ ...convertForm, notes: e.target.value })}
                disabled={converting}
                data-testid="sales-convert-notes"
              />
            </div>
            <div className="sales-calc__modal-footer">
              <button type="submit" className="sales-calc__btn" data-testid="sales-convert-submit" disabled={converting}>
                {converting ? "견적서 생성 중..." : "저장"}
              </button>
              <button type="button" className="sales-calc__btn sales-calc__btn--ghost" onClick={() => !converting && setConvertOpen(false)} disabled={converting}>취소</button>
            </div>
          </form>
        </div>
      )}
      {resetOpen && (
        <div className="sales-calc__reset-modal" data-testid="sales-calc-reset-modal">
          <div className="sales-calc__reset-card">
            <h4>현재 예상 견적을 모두 초기화하시겠습니까?</h4>
            <p className="text-sm text-white/70">선택한 홈페이지 유형, 기능, 수량 및 제안 금액이 모두 초기화됩니다.</p>
            <div className="flex gap-2">
              <button type="button" className="sales-calc__btn sales-calc__btn--ghost" onClick={() => setResetOpen(false)}>취소</button>
              <button type="button" className="sales-calc__btn sales-calc__btn--danger" data-testid="sales-calc-reset-submit" onClick={resetLocalSession}>전체 초기화</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
